import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import authService from '../../services/auth';
import { THEMES, getTheme, applyTheme } from '../../theme';
import BrandLogo from '../BrandLogo';

/**
 * Public marketing shell: sticky glass nav + footer, wrapping the landing and
 * pricing pages via <Outlet/>. Theme-aware — every colour resolves from the
 * active [data-theme] tokens, and a compact switcher lets visitors preview them.
 */
const NavItem = ({ to, children }) => (
  <NavLink
    to={to}
    end={to === '/'}
    className={({ isActive }) =>
      `px-3 py-2.5 text-sm font-medium rounded-lg transition-colors ${
        isActive ? 'text-white bg-gray-800' : 'text-gray-400 hover:text-white hover:bg-gray-800/60'
      }`
    }
  >
    {children}
  </NavLink>
);

const ThemeSwitcher = () => {
  const [theme, setTheme] = useState(getTheme());
  const choose = (id) => setTheme(applyTheme(id));
  return (
    <div className="hidden lg:flex items-center" role="group" aria-label="Colour theme">
      {THEMES.map((t) => (
        <button
          key={t.id}
          type="button"
          onClick={() => choose(t.id)}
          title={`${t.name} — ${t.tagline}`}
          aria-pressed={theme === t.id}
          className="flex h-11 w-11 items-center justify-center rounded-lg hover:bg-gray-800"
        >
          <span className={`h-4 w-4 rounded-full border ${theme === t.id ? 'border-white ring-2 ring-blue-400 ring-offset-4 ring-offset-gray-900' : 'border-gray-400'}`} style={{ background: t.gradient }} />
          <span className="sr-only">{t.name}</span>
        </button>
      ))}
    </div>
  );
};

const PublicLayout = () => {
  const { pathname, hash } = useLocation();
  const loggedIn = authService.isLoggedIn();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuButton = useRef(null);

  // Ensure a theme is applied even on a cold load of the public site.
  useEffect(() => {
    applyTheme(getTheme());
  }, []);

  // Reset scroll on route change so pricing always opens at the top.
  useEffect(() => {
    setMenuOpen(false);
    if (hash) {
      document.getElementById(hash.slice(1))?.scrollIntoView();
    } else {
      window.scrollTo({ top: 0, behavior: 'auto' });
    }
    document.title = pathname === '/pricing'
      ? 'Pricing & Plans | SageMindLabs'
      : 'SageMindLabs — Your Knowledge. Meaningful Answers.';
  }, [pathname, hash]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') {
        setMenuOpen(false);
        menuButton.current?.focus();
      }
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [menuOpen]);

  return (
    <div className="sm-marketing sm-app-bg min-h-screen flex flex-col text-gray-200">
      <a href="#main-content" className="sm-skip-link">Skip to content</a>
      <header className="sticky top-0 z-40 border-b border-gray-800/70 sm-glass">
        <nav aria-label="Main navigation" className="mx-auto max-w-6xl px-4 sm:px-6 h-20 flex items-center justify-between gap-3">
          <Link to="/" aria-label="SageMindLabs home" className="rounded-lg"><BrandLogo /></Link>
          <div className="hidden md:flex items-center gap-1">
            <NavItem to="/">Overview</NavItem>
            <a href="/#platform" className="rounded-lg px-3 py-2.5 text-sm text-gray-400 hover:text-white">Platform</a>
            <NavItem to="/pricing">Pricing</NavItem>
            <NavItem to="/help">Help</NavItem>
          </div>
          <div className="flex items-center gap-2">
            <ThemeSwitcher />
            <Link to={loggedIn ? '/dashboard' : '/login'} className="hidden md:block rounded-lg px-3 py-2.5 text-sm font-medium text-gray-300 hover:text-white">
              {loggedIn ? 'Dashboard' : 'Sign in'}
            </Link>
            <div className="hidden sm:block">
              <Link to={loggedIn ? '/dashboard' : '/register'} className="sm-primary-button text-sm">
                {loggedIn ? 'Open workspace' : 'Get started'} <span aria-hidden="true">↗</span>
              </Link>
            </div>
            <button ref={menuButton} type="button" className="md:hidden h-11 w-11 rounded-lg border border-gray-600 flex items-center justify-center hover:bg-gray-800" aria-label={menuOpen ? 'Close navigation' : 'Open navigation'} aria-expanded={menuOpen} aria-controls="mobile-navigation" onClick={() => setMenuOpen(!menuOpen)}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true"><path d={menuOpen ? 'M6 6l12 12M6 18L18 6' : 'M4 7h16M4 12h16M4 17h16'} /></svg>
            </button>
          </div>
        </nav>
        <nav id="mobile-navigation" aria-label="Mobile navigation" hidden={!menuOpen} className="md:hidden border-t border-gray-700 px-4 py-3" onClick={() => setMenuOpen(false)}>
          <div className="flex flex-col gap-1">
            <NavItem to="/">Overview</NavItem>
            <NavItem to="/pricing">Compare plans</NavItem>
            <NavItem to="/help">Help</NavItem>
            <NavItem to={loggedIn ? '/dashboard' : '/login'}>{loggedIn ? 'Dashboard' : 'Sign in'}</NavItem>
            {!loggedIn && <Link to="/register" className="sm-primary-button mt-2 justify-center">Create your workspace <span aria-hidden="true">↗</span></Link>}
          </div>
        </nav>
      </header>

      <main id="main-content" tabIndex={-1} className="flex-1">
        <Outlet />
      </main>

      <footer className="border-t border-gray-700/70 mt-8">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-12 flex flex-col md:flex-row justify-between gap-8">
          <div>
            <Link to="/" aria-label="SageMindLabs home" className="inline-block rounded-lg"><BrandLogo /></Link>
            <p className="mt-4 max-w-xs text-sm text-gray-400 leading-relaxed">Your knowledge, connected.<br />Your next conversation, made meaningful.</p>
            <p className="mt-6 text-xs text-gray-400">© {new Date().getFullYear()} SageMindLabs</p>
          </div>
          <nav aria-label="Footer navigation" className="flex gap-14 text-sm">
            <div className="flex flex-col gap-4">
              <span className="font-medium text-white">Explore</span>
              <a href="/#platform" className="text-gray-400 hover:text-white">Platform</a>
              <Link to="/pricing" className="text-gray-400 hover:text-white">Pricing</Link>
              <Link to="/help" className="text-gray-400 hover:text-white">Help Center</Link>
              <a href="/#how-it-works" className="text-gray-400 hover:text-white">How it works</a>
            </div>
            <div className="flex flex-col gap-4">
              <span className="font-medium text-white">Workspace</span>
              <Link to="/register" className="text-gray-400 hover:text-white">Get started</Link>
              <Link to="/login" className="text-gray-400 hover:text-white">Sign in</Link>
            </div>
          </nav>
        </div>
      </footer>
    </div>
  );
};

export default PublicLayout;
